/**
 * Die Rechnung hinter dem Verorten-Reiter: suchen, vorschlagen, Koordinaten lesen.
 *
 * `verorten.js` daneben wählt den Kartenausschnitt und schreibt die Position.
 * Hier steht, was davor kommt: Welche Plätze zeigt die Liste, welche Positionen
 * kommen als Vorschlag in Frage, und was bedeutet der Text, den jemand aus
 * Google Maps hereinkopiert.
 *
 * **Die Namensregeln spiegeln `src/tauchlogbuch/verortung.py`.** Dort stufen
 * sie den Bericht ein, hier füttern sie die Vorschläge — dieselben Schwellen,
 * dieselben Wächter. Wer eine ändert, ändert beide; die Tests auf beiden Seiten
 * prüfen dieselben Beispiele.
 */

import { hatKoordinaten } from './geo.js';
import { tagesanker } from './reisen.js';

// Ab hier gilt ein Name als Schreibvariante eines anderen (wie in der Pipeline).
export const AEHNLICH_AB = 0.88;

const UMLAUTE = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

const ROEMISCH = {
  i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6,
  vii: 7, viii: 8, ix: 9, x: 10, xi: 11, xii: 12,
};

/** Kleinschreibung, Umlaute ausgeschrieben, Satzzeichen weg. */
export function normalisiere(name) {
  if (!name) return '';
  const gefaltet = String(name).toLowerCase()
    .replace(/[äöüß]/g, (z) => UMLAUTE[z]);
  return gefaltet
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/**
 * Trennt den Kern von einer Endnummer: "Lopper III" -> ["lopper", 3].
 *
 * Die Schweizer Seeplätze sind durchnummeriert. Ohne diese Trennung kämen
 * "Lopper I" und "Lopper III" auf 0.94 Ähnlichkeit und würden als derselbe
 * Platz vorgeschlagen — sie liegen aber ein paar hundert Meter auseinander.
 */
export function zerlege(name) {
  const teile = normalisiere(name).split(' ').filter(Boolean);
  if (teile.length < 2) return [teile.join(' '), null];
  const letztes = teile[teile.length - 1];
  if (/^\d{1,2}$/.test(letztes)) return [teile.slice(0, -1).join(' '), Number(letztes)];
  if (ROEMISCH[letztes]) return [teile.slice(0, -1).join(' '), ROEMISCH[letztes]];
  return [teile.join(' '), null];
}

/** Zeichenweise Ähnlichkeit, 0 bis 1 — dieselbe Rechnung wie Pythons SequenceMatcher. */
export function aehnlichkeit(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const gleiche = passendeZeichen(a, b);
  return (2 * gleiche) / (a.length + b.length);
}

/**
 * Die Zahl übereinstimmender Zeichen nach Ratcliff/Obershelp: längste
 * gemeinsame Teilfolge, dann links und rechts davon rekursiv weiter.
 * Python rechnet in `difflib` genauso — deshalb treffen dort und hier
 * dieselben Schwellen.
 */
function passendeZeichen(a, b) {
  if (!a.length || !b.length) return 0;
  let bestA = 0;
  let bestB = 0;
  let bestLaenge = 0;
  let lauf = new Map();
  for (let i = 0; i < a.length; i += 1) {
    const neu = new Map();
    for (let j = 0; j < b.length; j += 1) {
      if (a[i] !== b[j]) continue;
      const laenge = (lauf.get(j - 1) ?? 0) + 1;
      neu.set(j, laenge);
      if (laenge > bestLaenge) {
        bestLaenge = laenge;
        bestA = i - laenge + 1;
        bestB = j - laenge + 1;
      }
    }
    lauf = neu;
  }
  if (bestLaenge === 0) return 0;
  return bestLaenge
    + passendeZeichen(a.slice(0, bestA), b.slice(0, bestB))
    + passendeZeichen(a.slice(bestA + bestLaenge), b.slice(bestB + bestLaenge));
}

/**
 * Wie nah sich zwei Kernnamen sind — `['zusatz'|'variante', wert]` oder `null`.
 *
 * Zwei Formen, weil zwei Dinge passieren. Zusatzwörter ("Elphinstone" gegen
 * "Elphinstone Reef") straft der Zeichenvergleich wegen der Längendifferenz ab
 * und fällt bei 0.815 durch jede vernünftige Schwelle — dafür die
 * Teilmengenprüfung. Schreibvarianten ("Zürisee" gegen "Zürichsee") erkennt
 * umgekehrt nur der Zeichenvergleich.
 *
 * Die Teilmenge verlangt dasselbe erste Wort: Sonst wäre "Reef" eine Teilmenge
 * von "Shark Reef" und jeder Riffname ein Treffer.
 */
export function namensnaehe(a, b) {
  if (!a || !b) return null;
  const wortA = a.split(' ');
  const wortB = b.split(' ');
  const mengeA = new Set(wortA);
  const mengeB = new Set(wortB);
  const teilmenge = [...mengeA].every((w) => mengeB.has(w))
    || [...mengeB].every((w) => mengeA.has(w));
  if (wortA[0] === wortB[0] && mengeA.size !== mengeB.size && teilmenge) {
    return ['zusatz', 1 - 0.01 * Math.abs(mengeA.size - mengeB.size)];
  }
  const wert = aehnlichkeit(a, b);
  return wert >= AEHNLICH_AB ? ['variante', wert] : null;
}

/**
 * Ist einer der beiden Namen ein Unterplatz des anderen?
 *
 * Der Schrägstrich ist im Bestand die Schreibweise für „Stelle X am Riff Y":
 * „Rainbow Reef / Ledge", „Flynn Reef / Gordons". 53 Plätze schreiben sich so,
 * bei 9 gibt es den Elternplatz ebenfalls als eigenen Satz.
 *
 * Geprüft wird auf dem **rohen** Namen, vor `normalisiere()`: Dort fällt der
 * Schrägstrich als Satzzeichen weg, und der Unterplatz sähe aus wie der
 * Elternplatz mit einem Zusatzwort — also wie ein Duplikat, mit einem
 * „Zusammenführen"-Knopf daneben. Er ist aber eine andere Stelle.
 *
 * Gegenstück in `src/tauchlogbuch/verortung.py:ist_teilplatz()` — wer eine
 * ändert, ändert beide.
 */
export function istTeilplatz(a, b) {
  if (!a || !b) return false;
  const eins = normalisiere(a);
  const zwei = normalisiere(b);
  return [[a, zwei], [b, eins]].some(([name, anderer]) => {
    const roh = String(name);
    if (!roh.includes(' / ')) return false;
    const kopf = normalisiere(roh.split(' / ')[0]);
    return Boolean(kopf) && kopf === anderer;
  });
}

/** Zwei bekannte, verschiedene Ländercodes schliessen einen Treffer aus. */
function laenderVertraeglich(a, b) {
  return !(a?.country_code && b?.country_code && a.country_code !== b.country_code);
}

/**
 * Woher eine Position stammt, als Wort.
 *
 * Die Schlüssel sind die Werte der Spalte `geocode_source` — genau so, wie sie
 * geschrieben werden. Steht hier `manuell` statt `manual`, fällt jeder von Hand
 * gesetzte Platz stumm auf die allgemeine Marke zurück und die Unterscheidung,
 * um die es geht, verschwindet. Deshalb steht die Tabelle hier und nicht in der
 * Ansicht: hier ist sie geprüft.
 */
export const HERKUNFT = {
  manual: 'von Hand',
  dive_gps: 'GPS',
  divinglog: 'Diving Log',
  nominatim: 'Geokodierung',
};

export function herkunftstext(platz) {
  return HERKUNFT[platz?.geocode_source] ?? null;
}

// ------------------------------------------------------------- Arbeitsliste

/**
 * Alle Plätze mit ihrer Tauchgangszahl, offene zuerst.
 *
 * Absteigend nach Tauchgangszahl: Wo vierzehn Tauchgänge dranhängen, lohnt
 * Genauigkeit mehr als bei einem einzelnen. Verortete kommen ans Ende, damit
 * sie die Arbeit nicht verstellen — sichtbar sind sie trotzdem, sonst liesse
 * sich eine falsch gesetzte Position nie wieder finden.
 */
export function arbeitsliste(plaetze, tauchgaenge) {
  const anzahl = new Map();
  const reisen = new Map();
  for (const tg of tauchgaenge) {
    if (!tg.site_id) continue;
    anzahl.set(tg.site_id, (anzahl.get(tg.site_id) ?? 0) + 1);
    if (tg.trip_name) {
      if (!reisen.has(tg.site_id)) reisen.set(tg.site_id, new Set());
      reisen.get(tg.site_id).add(tg.trip_name);
    }
  }
  return plaetze
    .map((platz) => ({
      platz,
      anzahl: anzahl.get(platz.id) ?? 0,
      reisen: [...(reisen.get(platz.id) ?? [])],
      verortet: hatKoordinaten(platz),
      manuell: platz.geocode_source === 'manual',
    }))
    .sort((a, b) => Number(a.verortet) - Number(b.verortet)
      || b.anzahl - a.anzahl
      || String(a.platz.name ?? '').localeCompare(String(b.platz.name ?? ''), 'de'));
}

/**
 * Ein Suchtext in Wörter — leer heisst "alles".
 *
 * Getrennt von der Prüfung, damit die Zerlegung nicht bei jedem der 580 Plätze
 * neu läuft.
 */
export function suchworte(text) {
  return normalisiere(text).split(' ').filter(Boolean);
}

/**
 * Passt ein Platz zur Suche?
 *
 * Alle Wörter müssen vorkommen, in beliebiger Reihenfolge und beliebigem Feld —
 * "lopper 14" findet den Platz nicht, "lopper" und "vierwald" beide. Gesucht
 * wird über Name, Land, Region, Gewässer, Notiz und die Reisen, weil man einen
 * Platz mal über den Namen und mal über "Malediven 2008" sucht.
 */
export function passt(platz, worte, reisen = []) {
  if (worte.length === 0) return true;
  const heuhaufen = normalisiere([
    platz?.name, platz?.country, platz?.region, platz?.water_name, platz?.notes, ...reisen,
  ].filter(Boolean).join(' '));
  return worte.every((wort) => heuhaufen.includes(wort));
}

/** Dieselbe Suche über die Arbeitsliste des Verorten-Reiters. */
export function filtere(eintraege, suchtext) {
  const worte = suchworte(suchtext);
  if (worte.length === 0) return eintraege;
  return eintraege.filter((eintrag) => passt(eintrag.platz, worte, eintrag.reisen));
}

// -------------------------------------------------------------- Vorschläge

const ART_TEXT = {
  namensgleich: 'gleicher Name',
  zusatz: 'Name mit Zusatzwort',
  variante: 'Schreibvariante',
  geschwister: 'gleicher Kern, andere Nummer',
  teilplatz: 'Stelle an diesem Platz',
  tagesnachbar: 'am selben Tag getaucht',
  reise: 'derselben Reise',
  nachbar: 'in der Nähe',
};

/**
 * Positionen, die für diesen Platz in Frage kommen — stärkste zuerst.
 *
 * Reihenfolge ist hier die halbe Miete: Ein Platz mit gleichem Namen ist fast
 * sicher derselbe, ein Platz derselben Reise liegt meist wenige Kilometer
 * entfernt, ein beliebiger Nachbar sagt nur "hier ungefähr". Alles landet in
 * derselben Liste, aber die Begründung steht dabei — geklickt wird mit
 * offenen Augen.
 *
 * Vorgeschlagen wird **nie automatisch gespeichert**: Der Klick setzt die
 * Nadel, gespeichert wird eigens.
 */
export function vorschlaege(platz, plaetze, tauchgaenge, grenze = 10) {
  if (!platz) return [];
  const verortete = plaetze.filter((p) => p.id !== platz.id && hatKoordinaten(p));
  const [zielKern, zielNummer] = zerlege(platz.name);
  const zielVoll = normalisiere(platz.name);
  const gefunden = new Map();

  const merke = (kandidat, art, wert) => {
    if (gefunden.has(kandidat.id)) return;
    gefunden.set(kandidat.id, {
      id: kandidat.id,
      name: kandidat.name ?? 'ohne Namen',
      lat: kandidat.lat,
      lon: kandidat.lon,
      art,
      begruendung: ART_TEXT[art],
      wert: wert ?? null,
    });
  };

  for (const kandidat of verortete) {
    if (!laenderVertraeglich(platz, kandidat)) continue;
    if (zielVoll && normalisiere(kandidat.name) === zielVoll) {
      merke(kandidat, 'namensgleich', 1);
      continue;
    }
    // Vor dem Namensvergleich: „Rainbow Reef / Ledge" ist eine Stelle am
    // Riff, kein zweiter Satz für dasselbe. Ohne diesen Wächter stünde daneben
    // ein „Zusammenführen"-Knopf.
    if (istTeilplatz(platz.name, kandidat.name)) {
      merke(kandidat, 'teilplatz', null);
      continue;
    }
    const [kern, nummer] = zerlege(kandidat.name);
    if (zielKern && kern === zielKern) {
      merke(kandidat, 'geschwister', null);
      continue;
    }
    if (nummer !== zielNummer) continue;
    const naehe = namensnaehe(zielKern, kern);
    if (naehe) merke(kandidat, naehe[0], naehe[1]);
  }

  // Plätze desselben Tages. Der stärkste Hinweis, den die Umgebung hergibt:
  // Am Bestand gemessen streuen sie im Median 25 km, die Plätze derselben
  // Reise 90 km. Deshalb stehen sie vor jenen und werden eigens benannt.
  for (const anker of tagesanker(platz, plaetze, tauchgaenge)) {
    if (!laenderVertraeglich(platz, anker)) continue;
    merke(anker, 'tagesnachbar', null);
  }

  // Plätze derselben Reise. Sie sagen nichts über den Namen, aber viel über
  // die Gegend: Ein Liveaboard fährt keine tausend Kilometer zwischen zwei
  // Tauchgängen.
  const meineReisen = new Set(tauchgaenge
    .filter((tg) => tg.site_id === platz.id && tg.trip_id)
    .map((tg) => tg.trip_id));
  if (meineReisen.size > 0) {
    const nachbarIds = new Set(tauchgaenge
      .filter((tg) => meineReisen.has(tg.trip_id) && tg.site_id !== platz.id)
      .map((tg) => tg.site_id));
    for (const kandidat of verortete) {
      if (nachbarIds.has(kandidat.id)) merke(kandidat, 'reise', null);
    }
  }

  const liste = [...gefunden.values()];
  const rang = {
    namensgleich: 0, zusatz: 1, variante: 1, geschwister: 2, teilplatz: 2,
    tagesnachbar: 3, reise: 4, nachbar: 5,
  };
  liste.sort((a, b) => rang[a.art] - rang[b.art] || (b.wert ?? 0) - (a.wert ?? 0));

  // Erst wenn gar nichts anderes da ist, die nächsten verorteten Plätze im
  // selben Land — besser als eine leere Karte, aber ehrlich benannt.
  if (liste.length === 0 && platz.country_code) {
    const imLand = verortete.filter((p) => p.country_code === platz.country_code);
    for (const kandidat of imLand.slice(0, grenze)) merke(kandidat, 'nachbar', null);
    return [...gefunden.values()].slice(0, grenze);
  }
  return liste.slice(0, grenze);
}

// ---------------------------------------------------------- Zusammenführen

/**
 * Welche Vorschläge einen Zusammenführen-Knopf verdienen.
 *
 * Nur Namenstreffer. `geschwister` ("Lopper I" zu "Lopper III"), `teilplatz`
 * ("Rainbow Reef / Ledge"), `tagesnachbar` und `reise` sind ausdrücklich
 * **andere** Plätze — dort einen Knopf anzubieten, hiesse die
 * Wächter zu umgehen, die genau diese Verwechslung verhindern sollen. Ein
 * `nachbar` ist nur ein Punkt in der Gegend.
 */
export const VERSCHMELZBAR = new Set(['namensgleich', 'zusatz', 'variante']);

export function darfVerschmelzen(vorschlag) {
  return VERSCHMELZBAR.has(vorschlag?.art);
}

/**
 * Welcher der beiden Sätze bleiben sollte.
 *
 * Der mit den meisten Tauchgängen: Sein Name steht schon an den meisten
 * Tauchgängen, also werden am wenigsten umbenannt. Bei Gleichstand gewinnt der
 * verortete — er trägt die Koordinaten, und ein Satz, der sie schon hat, ist
 * der ältere Anker. Das ist ein Vorschlag, keine Entscheidung: Gewählt wird im
 * Dialog, und zwar mit Namen.
 */
export function empfehlung(eins, zwei) {
  if (eins.anzahl !== zwei.anzahl) return eins.anzahl > zwei.anzahl ? eins : zwei;
  const einsVerortet = hatKoordinaten(eins.satz);
  const zweiVerortet = hatKoordinaten(zwei.satz);
  if (einsVerortet !== zweiVerortet) return einsVerortet ? eins : zwei;
  return eins;
}

/** "1 Tauchgang" oder "7 Tauchgänge" — an drei Stellen gebraucht, einmal geschrieben. */
export const tauchgangszahl = (anzahl) => (anzahl === 1 ? '1 Tauchgang' : `${anzahl} Tauchgänge`);

/** Dasselbe für Plätze, samt Verb: "1 weiterer Platz hat" / "29 weitere Plätze haben". */
export const weiterePlaetze = (anzahl) => (anzahl === 1
  ? '1 weiterer Platz hat' : `${anzahl} weitere Plätze haben`);

const wandert = (anzahl) => `${tauchgangszahl(anzahl)} ${anzahl === 1 ? 'wandert' : 'wandern'}`;

/**
 * Was das Zusammenführen bewirkt, in einem Satz.
 *
 * Beide Namen kommen vor und die Zahl der betroffenen Tauchgänge. Wer zwei
 * Stammsätze verschmilzt, löscht einen davon endgültig — das gehört
 * ausgeschrieben, nicht in ein "A und B zusammenführen?" verpackt.
 */
export function verschmelzfolgen(bleibt, weicht) {
  return `„${weicht.satz.name ?? 'ohne Namen'}" wird gelöscht`
    + (weicht.anzahl > 0
      ? `, ${wandert(weicht.anzahl)} zu „${bleibt.satz.name ?? 'ohne Namen'}"`
      : '')
    + '. Das lässt sich nicht zurücknehmen.';
}

// ----------------------------------------------------- Koordinaten einlesen

const ZAHL = String.raw`-?\d+(?:[.,]\d+)?`;
const TEIL = new RegExp(
  String.raw`(?<vor>[NSEWO])?\s*(?<grad>${ZAHL})\s*(?:°|º)?`
  + String.raw`(?:\s*(?<min>${ZAHL})\s*(?:'|′))?`
  + String.raw`(?:\s*(?<sek>${ZAHL})\s*(?:"|″|''))?`
  + String.raw`\s*(?<nach>[NSEWO])?`,
  'giu',
);

const zahl = (text) => (text === undefined ? 0 : Number(String(text).replace(',', '.')));

/**
 * Koordinaten aus hereinkopiertem Text — oder `null`.
 *
 * Wer eine Position nachträgt, hat sie meist in Google Maps gefunden und
 * kopiert von dort: mal "46.9757, 8.3403", mal 46°58'32.5"N 8°20'25.1"E, mal
 * die ganze Adresszeile mit @46.9757,8.3403,15z darin. Alle drei müssen
 * ankommen, sonst tippt man Zahlen ab und vertut sich dabei.
 *
 * Die Himmelsrichtung entscheidet, was Breite und was Länge ist; ohne sie gilt
 * die übliche Reihenfolge Breite vor Länge. Ausserhalb des gültigen Bereichs
 * kommt `null` zurück — lieber nichts als eine Nadel im Nirgendwo.
 */
export function leseKoordinaten(text) {
  if (typeof text !== 'string' || !text.trim()) return null;
  const roh = text.trim();

  // Kartenverweise zuerst: Darin stehen Zahlen (Zoomstufe, Kachel-Ids), die
  // der allgemeine Leser unten fälschlich für Koordinaten hielte.
  if (/https?:\/\//i.test(roh) || /maps\./i.test(roh)) {
    const ausUrl = roh.match(/[@=](-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)/);
    return ausUrl ? pruefe(Number(ausUrl[1]), Number(ausUrl[2])) : null;
  }

  const teile = [];
  for (const treffer of roh.matchAll(TEIL)) {
    const { vor, grad, min, sek, nach } = treffer.groups;
    if (grad === undefined) continue;
    const richtung = (vor ?? nach ?? '').toUpperCase();
    const betrag = Math.abs(zahl(grad)) + zahl(min) / 60 + zahl(sek) / 3600;
    const negativ = zahl(grad) < 0 || richtung === 'S' || richtung === 'W';
    teile.push({ wert: negativ ? -betrag : betrag, richtung });
    if (teile.length === 2) break;
  }
  if (teile.length < 2) return null;

  const breite = teile.find((t) => t.richtung === 'N' || t.richtung === 'S');
  const laenge = teile.find((t) => ['E', 'W', 'O'].includes(t.richtung));
  return breite && laenge
    ? pruefe(breite.wert, laenge.wert)
    : pruefe(teile[0].wert, teile[1].wert);
}

function pruefe(lat, lon) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  if (lat === 0 && lon === 0) return null;   // Nullmeridian, kein Tauchplatz
  return { lat, lon };
}
